import {
  mapAmountDocumentToPageModel,
  mapAmountPageModelToRequest,
} from "../../ui/widgets/amount/mapper";
import { GeneralCompanyInformationModel, YesNo } from "./types";
import {
  mapUserToCorrespondenceLanguage,
  mapYesNoStringToBoolean,
} from "../shared/mappers";
import {
  LanguageBackendType,
  LanguageFrontendType,
} from "../shared/components/language/types";
import { defaultAddress } from "../shared/defaults";
import { formatFullStreet } from "../../util/format";
import {
  BasicAccountApplicationDocument,
  FlexibleInvoicing,
  GeneralCompanyInformationDto,
} from "../../api/commercial/generated-types";
import { FlexibleInvoicingModel } from "./options/flexible-invoicing/types";
import { Product } from "../../context/product-context/product";
import { LegalForm } from "../home/types";

const mapHasDecentralizedInvoicing = (
  hasDecentralizedInvoicingFeature: boolean,
  hasDecentralizedInvoicing: boolean,
  legalForm: LegalForm,
): boolean => {
  if (!hasDecentralizedInvoicingFeature) {
    return false;
  }

  if ([LegalForm.FOUNDATION, LegalForm.ASSOCIATION].includes(legalForm)) {
    return true;
  }

  return hasDecentralizedInvoicing;
};

const mapFlexibleInvoicingToRequest = (
  flexibleInvoicing: FlexibleInvoicingModel,
  hasDecentralizedInvoicing: boolean,
) => {
  if (flexibleInvoicing) {
    return {
      address:
        flexibleInvoicing.hasFlexibleInvoicingAddress &&
        !hasDecentralizedInvoicing
          ? flexibleInvoicing.address
          : null,
    };
  }

  return null;
};

const mapFlexibleInvoicingToModel = (
  flexibleInvoicing: FlexibleInvoicing,
): FlexibleInvoicingModel => {
  return {
    hasFlexibleInvoicingAddress: !!flexibleInvoicing?.address,
    fullStreet: flexibleInvoicing
      ? formatFullStreet(flexibleInvoicing.address)
      : "",
    address:
      flexibleInvoicing && flexibleInvoicing.address
        ? flexibleInvoicing.address
        : defaultAddress(),
  };
};

const mapPageModelToRequest = (
  data: GeneralCompanyInformationModel,
  basicAccountApplicationRef: string,
): GeneralCompanyInformationDto => {
  return {
    basicAccountApplicationRef,
    limit: mapAmountPageModelToRequest(data.limit),
    expectedSpendingPerMonth: mapAmountPageModelToRequest(
      data.expectedSpendingPerMonth,
    ),
    hasCompanyOnCard: mapYesNoStringToBoolean(data.hasCompanyOnCard),
    correspondenceLanguage: data.correspondenceLanguage,
    iban: data.iban,
    bankCode: data.bankCode,
    bankName: data.bankName,
    flexibleInvoicing: mapFlexibleInvoicingToRequest(
      data.flexibleInvoicing,
      data.hasDecentralizedInvoicing,
    ),
    hasDecentralizedInvoicing: data.hasDecentralizedInvoicing,
    decentralizedInvoicingAddress: data.decentralizedInvoicingAddress,
  };
};

const mapDocumentToPageModel = (
  document: BasicAccountApplicationDocument,
  language: LanguageFrontendType,
  selectedProduct: Product,
): GeneralCompanyInformationModel => {
  const hasFlexibleInvoicingFeature =
    selectedProduct?.hasFeature("FLEXIBLE_INVOICING");
  const hasDecentralizedInvoicingFeature = selectedProduct?.hasFeature(
    "DECENTRALIZED_INVOICING",
  );

  const hasDecentralizedInvoicing = mapHasDecentralizedInvoicing(
    hasDecentralizedInvoicingFeature,
    document.hasDecentralizedInvoicing,
    document?.company?.legalForm as LegalForm,
  );
  return {
    limit: mapAmountDocumentToPageModel(document.limit),
    expectedSpendingPerMonth: mapAmountDocumentToPageModel(
      document.expectedSpendingPerMonth,
    ),
    hasCompanyOnCard: document.hasCompanyOnCard ? YesNo.YES : YesNo.NO,
    correspondenceLanguage:
      (document.company.correspondenceLanguage as LanguageBackendType) ||
      mapUserToCorrespondenceLanguage(language),
    iban: document.bankAccount ? document.bankAccount.iban : "",
    bankCode: document.bankAccount ? document.bankAccount.bankCode : "",
    bankName: document.bankAccount ? document.bankAccount.bankName : "",
    flexibleInvoicing: hasFlexibleInvoicingFeature
      ? mapFlexibleInvoicingToModel(document.flexibleInvoicing)
      : null,
    legalForm: document?.company?.legalForm as LegalForm,
    hasDecentralizedInvoicing: hasDecentralizedInvoicing,
    decentralizedInvoicingAddress: hasDecentralizedInvoicing
      ? document.decentralizedInvoicingAddress
      : null,
  };
};

const GeneralCompanyInformationMapper = {
  mapPageModelToRequest,
  mapDocumentToPageModel,
};

export default GeneralCompanyInformationMapper;
